/**
 * Likert items: per-item summaries, composite scores and the diverging stacked bar chart.
 *
 * Codes run from `low` (1, or 0 for 0–4 coding) upwards, one per scale point. A cell may
 * also hold the option's label ("Agree") — Google Forms and most survey platforms export
 * text, not codes — and is read as that option's code.
 *
 * Medians, modes and percentages treat the responses as what they are: ordered
 * categories. Means and standard deviations treat the codes as equally spaced numbers,
 * which is common practice and defensible for multi-item composites but an assumption
 * for a single item; the widget says so beside them.
 */

import { parseNumberInput } from "@/lib/parse-number";
import { mean, median, standardDeviation, sum } from "@/lib/stats/descriptive";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";
import { isMissingToken } from "@/lib/tool-kit/research-design/matrix";

export type LikertPoints = 4 | 5 | 7;

export interface LikertScale {
  points: LikertPoints;
  /** The code of the first option: 1 for 1–5 coding, 0 for 0–4. */
  low: number;
}

export function highCode(scale: LikertScale): number {
  return scale.low + scale.points - 1;
}

/** The code a cell stands for: a number on the scale, or one of its labels. */
export function codeOf(
  cell: string,
  scale: LikertScale,
  labels: readonly string[]
): number | null {
  const text = cell.trim().toLowerCase();
  const byLabel = labels.findIndex(
    (label) => label.trim().toLowerCase() === text
  );
  if (byLabel >= 0 && byLabel < scale.points) {
    return scale.low + byLabel;
  }
  const value = parseNumberInput(cell);
  if (
    value === null ||
    !Number.isInteger(value) ||
    value < scale.low ||
    value > highCode(scale)
  ) {
    return null;
  }
  return value;
}

export interface LikertCodes {
  /** Respondents × items; null where an answer is missing or unusable. */
  values: (number | null)[][];
  /** Blank or "NA"-style cells. */
  missing: number;
  /** Cells that are neither a code on this scale nor one of its labels. */
  invalid: number;
}

export function readCodes(
  body: readonly (readonly string[])[],
  scale: LikertScale,
  labels: readonly string[]
): LikertCodes {
  let missing = 0;
  let invalid = 0;
  const values = body.map((row) =>
    row.map((cell) => {
      if (isMissingToken(cell)) {
        missing += 1;
        return null;
      }
      const code = codeOf(cell, scale, labels);
      if (code === null) {
        invalid += 1;
      }
      return code;
    })
  );
  return { values, missing, invalid };
}

export interface LikertItemSummary {
  /** Valid answers. */
  n: number;
  /** Answers per option, lowest code first. */
  counts: number[];
  median: number;
  /** Every code that ties for the highest count. */
  modes: number[];
  mean: number;
  /** Sample standard deviation (n − 1). */
  sd: number;
  /** Shares (0–1) below, at and above the midpoint; `neutral` is null on an even scale. */
  disagree: number;
  neutral: number | null;
  agree: number;
  /** Share choosing one of the two highest options. */
  topTwo: number;
}

export function itemColumn(
  values: readonly (readonly (number | null)[])[],
  item: number
): (number | null)[] {
  return values.map((row) => row[item] ?? null);
}

export function summarizeItem(
  column: readonly (number | null)[],
  scale: LikertScale
): LikertItemSummary {
  const answers = column.filter((value): value is number => value !== null);
  const n = answers.length;
  const counts = new Array<number>(scale.points).fill(0);
  for (const value of answers) {
    counts[value - scale.low] += 1;
  }
  const top = Math.max(...counts);
  const modes =
    top > 0
      ? counts.flatMap((count, i) => (count === top ? [scale.low + i] : []))
      : [];
  const share = (from: number, to: number) =>
    n === 0 ? Number.NaN : sum(counts.slice(from, to)) / n;
  const half = Math.floor(scale.points / 2);
  const odd = scale.points % 2 === 1;
  return {
    n,
    counts,
    median: n === 0 ? Number.NaN : median(answers),
    modes,
    mean: mean(answers),
    sd: standardDeviation(answers),
    disagree: share(0, half),
    neutral: odd ? share(half, half + 1) : null,
    agree: share(odd ? half + 1 : half, scale.points),
    topTwo: share(scale.points - 2, scale.points),
  };
}

/* ------------------------------------------------------------ composites */

export type CompositeMethod = "sum" | "mean";

export interface CompositeScore {
  /** Items this respondent answered. */
  answered: number;
  /** Null when there is nothing to score — or, for a sum, when an item is missing. */
  score: number | null;
}

/** A reverse-coded answer: (lowest code + highest code) − answer. */
export function reverseCode(value: number, scale: LikertScale): number {
  return scale.low + highCode(scale) - value;
}

/**
 * One score per respondent.
 *
 * A sum is only comparable across people who answered every item, so a respondent with
 * a gap gets no sum. A mean is taken over the items answered — the usual "person mean"
 * convention — and the widget shows how many that was.
 */
export function compositeScores(
  values: readonly (readonly (number | null)[])[],
  itemCount: number,
  reversed: ReadonlySet<number>,
  scale: LikertScale,
  method: CompositeMethod
): CompositeScore[] {
  return values.map((row) => {
    let total = 0;
    let answered = 0;
    for (let item = 0; item < itemCount; item++) {
      const value = row[item] ?? null;
      if (value === null) {
        continue;
      }
      total += reversed.has(item) ? reverseCode(value, scale) : value;
      answered += 1;
    }
    if (answered === 0) {
      return { answered, score: null };
    }
    if (method === "sum") {
      return { answered, score: answered === itemCount ? total : null };
    }
    return { answered, score: total / answered };
  });
}

/* ----------------------------------------------------------------- chart */

/** One option's slice of a diverging bar, in shares of the item's answers. */
export interface DivergingSegment {
  code: number;
  /** Left edge, relative to the centre line (negative = disagree side). */
  start: number;
  share: number;
  count: number;
}

/**
 * The diverging stacked bar for one item (Robbins & Heiberger): disagreement runs left of
 * the centre line, agreement right, and the neutral option straddles it, half each side.
 */
export function divergingSegments(
  summary: LikertItemSummary,
  scale: LikertScale
): DivergingSegment[] {
  if (summary.n === 0) {
    return [];
  }
  let x = -(summary.disagree + (summary.neutral ?? 0) / 2);
  return summary.counts.map((count, i) => {
    const share = count / summary.n;
    const segment = { code: scale.low + i, start: x, share, count };
    x += share;
    return segment;
  });
}

/** Half the axis width: the widest side of any bar, rounded up to a quarter. */
export function divergingExtent(
  bars: readonly (readonly DivergingSegment[])[]
): number {
  let widest = 0;
  for (const bar of bars) {
    const last = bar.at(-1);
    if (!last) {
      continue;
    }
    widest = Math.max(widest, -bar[0].start, last.start + last.share);
  }
  return Math.min(1, Math.max(0.25, Math.ceil(widest * 4 - 1e-9) / 4));
}

/** Okabe–Ito vermillion for disagreement, blue for agreement, grey in the middle. */
const DISAGREE_POLE = CHART_COLORS[3];
const AGREE_POLE = CHART_COLORS[0];
export const NEUTRAL_FILL = "#D9D9D9";

/** How far each step of an arm is mixed toward white, strongest option first. */
const ARM_TINTS: Record<number, readonly number[]> = {
  2: [0, 0.5],
  3: [0, 0.35, 0.65],
};

function channels(hex: string): number[] {
  return [1, 3, 5].map((at) => Number.parseInt(hex.slice(at, at + 2), 16));
}

function mixWithWhite(hex: string, amount: number): string {
  const mixed = channels(hex).map((value) =>
    Math.round(value + (255 - value) * amount)
      .toString(16)
      .padStart(2, "0")
  );
  return `#${mixed.join("").toUpperCase()}`;
}

/**
 * Fill colours for the options, lowest code first: lightness rises from each pole toward
 * the grey midpoint, so the order of the options is visible without the legend.
 */
export function likertColors(points: LikertPoints): string[] {
  const steps = ARM_TINTS[Math.floor(points / 2)];
  const disagree = steps.map((amount) => mixWithWhite(DISAGREE_POLE, amount));
  const agree = [...steps]
    .reverse()
    .map((amount) => mixWithWhite(AGREE_POLE, amount));
  return points % 2 === 1
    ? [...disagree, NEUTRAL_FILL, ...agree]
    : [...disagree, ...agree];
}

function luminance(hex: string): number {
  const [r, g, b] = channels(hex).map((value) => {
    const c = value / 255;
    return c <= 0.040_45 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrast(a: string, b: string): number {
  const [light, dark] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (light + 0.05) / (dark + 0.05);
}

/** White or near-black, whichever reads better on a fill. */
export function inkOn(fill: string): string {
  return contrast(fill, "#FFFFFF") > contrast(fill, CHART_INK.text)
    ? "#FFFFFF"
    : CHART_INK.text;
}
